import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import useApiCall from 'hooks/use-api-call';
import { AdminApi } from 'api/apis';
import LoadingRing from 'ui/atoms/loading-ring';
import { AdminIssuerDetail } from 'api/models';
import BoxedContent from 'ui/molecules/boxed-content';
import IssuerData from './issuer-data-view';
import Number from 'ui/atoms/number';
import CopyButton from 'ui/molecules/copy-button';
import IssuerDetailsProducts from 'apps/issuer/pages/issuer-details/issuer-products';
import { currentIssuerHasEditIssuerPermission } from 'apps/issuer/helpers/issuerPermissions';
import CardItem from 'src/ui/atoms/card-item';
import Link from 'core/routing/link';
import ActionButtons from 'src/ui/molecules/action-buttons';
import PlaceholderFallback from 'src/ui/atoms/placeholder-fallback';
import PageWrapper from 'src/core/layout/content/page-wrapper';

interface IssuerIssuerDetailsPageProps {
  issuerId: string;
}

const IssuerIssuerDetailsPage: FunctionComponent<IssuerIssuerDetailsPageProps> = ({ issuerId }) => {
  const [issuer, setIssuer] = useState<AdminIssuerDetail>();
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  const { withApi, makeAuthenticatedApi } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const fetchIssuerDetails = useCallback(() => {
    withApi(async () => {
      const issuerDetails = await adminApi.adminIssuersRetrieve({ id: issuerId });
      setIssuer(issuerDetails);
      // get products of an issuer
    });
  }, [issuerId]);

  useEffect(() => {
    fetchIssuerDetails();
  }, [withApi, adminApi, issuerId]);

  const canEdit = useMemo(() => currentIssuerHasEditIssuerPermission(issuer?.permissions || []), [issuer?.permissions]);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent
        title={<Translate name="dashboardIssuerIssuerDetails.title" args={[issuer?.legalPerson?.companyName]} />}
        titleSpacing="larger"
      >
        <>
          {!issuer && <LoadingRing />}
          {issuer && (
            <>
              <BoxedContent releaseSpaceWhenTitleIsEmpty={true} title={<Translate name="common.generalInformation" />}>
                <>
                  <CardItem
                    title={<Translate name="dashboardIssuerIssuerDetails.issuerId" />}
                    content={issuerId}
                    icon={<CopyButton valueToCopy={issuerId} />}
                  />
                  <CardItem
                    title={<Translate name="dashboardIssuerIssuerDetails.organization" />}
                    content={<PlaceholderFallback>{issuer.organization?.name}</PlaceholderFallback>}
                  />
                  <CardItem
                    title={<Translate name="dashboardIssuerIssuerDetails.productNumber" />}
                    content={<Number>{issuer.numberOfProducts}</Number>}
                  />
                </>
              </BoxedContent>
              <IssuerData issuer={issuer} canEdit={canEdit} fetchIssuerDetails={fetchIssuerDetails} />
            </>
          )}
          <IssuerDetailsProducts
            issuerId={issuerId}
            numberOfProducts={issuer?.numberOfProducts}
            setFilterPopupHeight={setFilterPopupHeight}
          />
          <ActionButtons align="start">
            <Link icon="arrow-left" to="/dashboard/issuers">
              <Translate as="span" name="dashboardIssuerIssuerDetails.backToIssuers" />
            </Link>
          </ActionButtons>
        </>
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerIssuerDetailsPage, AppType.ISSUER);
